import * as React from 'react';
import { Dialog, DialogContent, DialogContentText, DialogTitle, useMediaQuery, Typography, Divider, Box, Button, IconButton, Grid, TextField, CardMedia } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// ✅ TEMPORAL: horas de prueba por día de la semana, solo para tener la UI
// funcionando. Cuando esté lista la API nueva de disponibilidad de horarios,
// esto se reemplaza por una llamada que reciba `selectedDate` y devuelva las
// horas disponibles reales para ese día.
const availableHours = [
  { date: "Monday", hours: ["10:00"] },
  { date: "Tuesday", hours: ["10:00", "11:00", "12:30"] },
  { date: "Wednesday", hours: ["09:00", "10:00", "11:00", "12:30"] },
  { date: "Thursday", hours: ["10:00", "11:00", "12:30", "16:30"] },
  { date: "Friday", hours: ["10:00", "11:00", "12:30"] },
  { date: "Saturday", hours: ["10:00", "11:00", "12:30"] },
  { date: "Sunday", hours: ["10:00"] },
];

// ✅ TEMPORAL: cupo de ejemplo por horario, solo para tener la validación de
// "no reservar más gente de la que hay cupo" funcionando. Cuando esté lista la
// API de disponibilidad, esto se reemplaza por el cupo real que devuelva el
// backend para la fecha y hora elegidas.
const mockCuposPorHora = {
  "09:00": 10,
  "10:00": 4,
  "11:00": 8,
  "12:30": 2,
  "16:30": 0,
};

export default function DialogMoreInfoActivity({ open, handleClose, activity }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedDate, setSelectedDate] = React.useState(dayjs());
  const [selectedHour, setSelectedHour] = React.useState(null);
  const [selectedAdults, setSelectedAdults] = React.useState(1);
  const [selectedChildren, setSelectedChildren] = React.useState(0);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedHour(null);  // Reset selected hour when date changes
  };

  const handleHourSelect = (hour) => {
    setSelectedHour(hour);
  };

  const getAvailableHours = (date) => {
    const day = date.format('dddd');
    const hoursForDay = availableHours.find((entry) => entry.date === day);
    return hoursForDay ? hoursForDay.hours : [];
  };

  const getCupoDisponible = (hour) => (hour ? mockCuposPorHora[hour] ?? null : null);

  const selectedDayHours = getAvailableHours(selectedDate);
  // null = todavía no se eligió hora, o el horario no tiene límite conocido.
  const cupoDisponible = getCupoDisponible(selectedHour);
  const totalPax = selectedAdults + selectedChildren;

  // ✅ Si cambia el horario (y por lo tanto el cupo disponible) y la cantidad de
  // gente ya cargada supera el nuevo cupo, la ajustamos para que nunca quede por
  // encima de lo disponible.
  React.useEffect(() => {
    if (cupoDisponible == null) return;
    setSelectedAdults((prevAdults) => Math.min(prevAdults, cupoDisponible));
    setSelectedChildren((prevChildren) => {
      const maxChildren = Math.max(0, cupoDisponible - Math.min(selectedAdults, cupoDisponible));
      return Math.min(prevChildren, maxChildren);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cupoDisponible]);

  const handleAdultsChange = (value) => {
    let next = Math.max(0, value);
    if (cupoDisponible != null) {
      next = Math.min(next, Math.max(0, cupoDisponible - selectedChildren));
    }
    setSelectedAdults(next);
  };

  const handleChildrenChange = (value) => {
    let next = Math.max(0, value);
    if (cupoDisponible != null) {
      next = Math.min(next, Math.max(0, cupoDisponible - selectedAdults));
    }
    setSelectedChildren(next);
  };

  // ✅ El precio es el mismo para todos los horarios de una actividad, así que
  // no depende de qué hora se elija. Mientras la API no lo entregue, mostramos
  // "Consultar".
  const priceLabel = activity?.price ? `${activity?.currency} ${activity?.price}` : 'Consultar';

  const sinCupo = cupoDisponible === 0;
  const noSePuedeComprar = !selectedHour || sinCupo || totalPax === 0;

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle id="responsive-dialog-title">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {activity?.activityName}
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* ✅ Una única foto, sin carrusel */}
        <CardMedia
          component="img"
          image={activity?.image}
          alt={activity?.activityName}
          sx={{ width: '100%', maxHeight: 320, objectFit: 'cover', borderRadius: 2 }}
        />
        <DialogContent>
          <Typography variant="h6" component="div">
            Detalle de la actividad
          </Typography>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {activity?.description}
          </DialogContentText>
        </DialogContent>
        <DialogContent>
          <Typography variant="h6" component="div">
            Selección de fecha y hora para la actividad
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              label="¿Cuándo voy?"
            />
          </LocalizationProvider>

          <Typography variant="h6" component="div" sx={{ mt: 2 }}>
            Horas disponibles:
          </Typography>
          {selectedDayHours.length > 0 ? (
            selectedDayHours.map((hour, index) => (
              <Button
                key={index}
                variant={selectedHour === hour ? "contained" : "outlined"}
                sx={{ m: 1 }}
                onClick={() => handleHourSelect(hour)}
              >
                {hour}
              </Button>
            ))
          ) : (
            <Typography variant="body1" color="text.secondary">
              No hay horas disponibles para el día seleccionado.
            </Typography>
          )}

          {/* ✅ El cupo solo se muestra cuando queda poco (5 o menos), para no
              saturar la pantalla cuando hay disponibilidad de sobra. */}
          {selectedHour && cupoDisponible != null && cupoDisponible <= 5 && (
            <Typography
              variant="body2"
              sx={{ mt: 1, fontWeight: 600, color: sinCupo ? 'error.main' : 'warning.main' }}
            >
              {sinCupo
                ? 'No quedan cupos disponibles para este horario.'
                : `¡Quedan solo ${cupoDisponible} cupo(s) disponibles para este horario!`}
            </Typography>
          )}

          <Grid container spacing={2} sx={{ mt: 1.5 }}>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                Adultos
              </Typography>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, max: cupoDisponible != null ? cupoDisponible : undefined }}
                value={selectedAdults}
                onChange={(e) => handleAdultsChange(Number(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                Menores
              </Typography>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, max: cupoDisponible != null ? cupoDisponible : undefined }}
                value={selectedChildren}
                onChange={(e) => handleChildrenChange(Number(e.target.value) || 0)}
              />
            </Grid>
            {cupoDisponible != null && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Máximo {cupoDisponible} persona(s) (adultos + menores) para el horario seleccionado.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <Divider />
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <DialogContentText variant="h6" component="div">
                Precio {priceLabel}
              </DialogContentText>
              <Button variant="contained" color="primary" sx={{ mt: 2 }} disabled={noSePuedeComprar}>
                Comprar
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </DialogContent>
    </Dialog>
  );
}